import CaseHelper from '@/Case/CaseHelper';
import LeadHelper from '@/CRM/Sales/LeadHelper';
import {getPayerIds} from '@/Case/caseParties';
import {existsFilter} from '@/lib/picos/filters';
import type {uuid} from '@/@types/entity';
import type Contact from '@/entities/contact';
import type Lead from '@/entities/lead';
import type Matter from '@/entities/matter';
import type Transaction from '@/entities/transaction';
import type Participant from '@/entities/participant';
import type ArrayMap from '@/lib/ArrayMap';

/** The subset of Transaction fields needed to resolve a payment's payer-fetch set. */
export type PayerFetchCurrent = Pick<Transaction,
    'clientIds' | 'entityId' | 'entityType' | 'matterIds' | 'source' | 'sourceType'>;

/**
 * Compute the set of contact ids whose stored payment methods should be fetched for a
 * transaction's payer dropdown.
 *
 * Includes the case's people — the client, a lead's clients/payers, the matter's clients and
 * its benefactors — AND the contact actually selected as payer (`current.source`). FR/PC
 * transactions allow any contact to be the source (commit 0cbfd7289 widened the payer
 * dropdown), but the fetch was never widened to match, so a payer who wasn't a client or
 * benefactor of the case showed no saved cards even with a valid one on file (OWA-485).
 *
 * `participants` is the subscribed participants slice; it is passed through to
 * `getPayerIds` so benefactor detection is reactive to records that sync in after mount.
 * The returned list may contain duplicates — the caller dedups before fetching.
 */
export function getPayerFetchContactIds(
    client: Contact | null | undefined,
    lead: Lead | null | undefined,
    matter: Matter | null | undefined,
    current: PayerFetchCurrent,
    participants?: ArrayMap<Participant> | Participant[],
): uuid[] {
    const contactIds: uuid[] = [];
    if (client) contactIds.push(client.id);

    // OWA-1341: clients and benefactors (including a Lead's Benefactor participants) come from
    // the shared caseParties rule.
    const cases = [lead, matter].filter(existsFilter);
    if (cases.length > 0) contactIds.push(...getPayerIds(cases, participants));

    if (contactIds.length === 0) {
        contactIds.push(...current.clientIds);
        const fromTransaction = current.entityType === 'lead'
                                ? [LeadHelper.getLeadFromId(current.entityId)]
                                : current.matterIds.map(mid => CaseHelper.getMatterFromId(mid));
        contactIds.push(...getPayerIds(fromTransaction, participants));
    }

    // OWA-485: always include the contact actually selected as payer. Placed after the
    // empty-set fallback so it never suppresses the case-people lookup above.
    if (current.source && current.sourceType === 'C') contactIds.push(current.source);

    return contactIds;
}
